import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ApiKeyService } from '../../services/api-key.service';
import { NectaOneApiKeyDetailsDto } from '../../models/api-key.model';

@Injectable({ providedIn: 'root' })
export class ApiKeyStore {
  private readonly apiKeyService = inject(ApiKeyService);
  private readonly messageService = inject(MessageService);

  apiKeys: WritableSignal<NectaOneApiKeyDetailsDto[]> = signal([]);
  isLoading: WritableSignal<boolean> = signal(false);
  newlyCreatedKey: WritableSignal<string | null> = signal(null);

  loadApiKeys(): void {
    this.isLoading.set(true);
    this.apiKeyService.getApiKeys().subscribe({
      next: (keys) => {
        this.apiKeys.set(keys);
        this.isLoading.set(false);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Fehler',
          detail: 'API-Keys konnten nicht geladen werden.',
        });
        this.isLoading.set(false);
      },
    });
  }

  createApiKey(label?: string | null): void {
    this.isLoading.set(true);
    this.apiKeyService.createApiKey({ label }).subscribe({
      next: (result) => {
        this.newlyCreatedKey.set(result.apiKey);
        this.messageService.add({
          severity: 'success',
          summary: 'Erstellt',
          detail: 'API-Key wurde erfolgreich erstellt.',
        });
        this.loadApiKeys();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Fehler',
          detail: 'API-Key konnte nicht erstellt werden.',
        });
        this.isLoading.set(false);
      },
    });
  }

  rotateApiKey(keyHash: string): void {
    this.isLoading.set(true);
    this.apiKeyService.rotateApiKey(keyHash).subscribe({
      next: (result) => {
        this.newlyCreatedKey.set(result.apiKey);
        this.messageService.add({
          severity: 'success',
          summary: 'Erneuert',
          detail: 'API-Key wurde erfolgreich erneuert.',
        });
        this.loadApiKeys();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Fehler',
          detail: 'API-Key konnte nicht erneuert werden.',
        });
        this.isLoading.set(false);
      },
    });
  }

  revokeApiKey(apiKeyId: string): void {
    this.isLoading.set(true);
    this.apiKeyService.revokeApiKey(apiKeyId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Widerrufen',
          detail: 'API-Key wurde erfolgreich widerrufen.',
        });
        this.newlyCreatedKey.set(null);
        this.loadApiKeys();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Fehler',
          detail: 'API-Key konnte nicht widerrufen werden.',
        });
        this.isLoading.set(false);
      },
    });
  }

  clearNewKey(): void {
    this.newlyCreatedKey.set(null);
  }
}